import React from "react";
import { useTranslation } from "react-i18next";

import { useAppSelector, useAppDispatch } from "store/index";
import {
  TournamentContentItem,
  handleUpdateFields,
} from "store/liveOps/tournaments/slice";
import InnerInput from "components/inputs/InnerInput/InnerInput";
import SwitchBtn from "components/buttons/SwitchBtn";
import TextEditor from "components/wysiwyg/Wysiwyg";
import { ImageUpload } from "components/imageUpload";

interface Props {
  activeLocale: string;
  setActiveLocale: (e: string) => void;
  fieldErrors?: Record<string, string>;
  clearFieldError: (field: string) => void;
  readOnly?: boolean;
}

const TournamentsContentTab: React.FC<Props> = ({
  activeLocale,
  setActiveLocale,
  fieldErrors,
  clearFieldError,
  readOnly = false,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const localesList = useAppSelector((store) => store.settings.locales);
  const payload = useAppSelector((store) => store.tournaments.payload);
  const currentItem = payload?.contents?.find(
    (el) => el?.locale === activeLocale,
  );

  const handleDataContent = (
    key: keyof TournamentContentItem,
    value: string | number | boolean | File,
  ) => {
    if (readOnly || !payload) return;

    const contents = payload.contents ?? [];
    const existing = contents.find((el) => el?.locale === activeLocale);
    const nextItem: TournamentContentItem = {
      ...(existing ?? { locale: activeLocale, content: "" }),
      locale: activeLocale,
      [key]: value,
    };

    dispatch(
      handleUpdateFields({
        ...payload,
        contents: [
          ...contents.filter((el) => el?.locale !== activeLocale),
          nextItem,
        ],
      }),
    );
  };

  return (
    <div className="tournament-rules">
      <div className="game-modal-tabs bonus-currencies-tabs small">
        {localesList?.map((el) => (
          <div
            className={`tab-item ${activeLocale === el ? "active" : ""}`}
            key={el}
            onClick={() => {
              setActiveLocale(el);
            }}
          >
            <img
              src={`https://flagpedia.net/data/flags/icon/16x12/${el
                .split("-")[1]
                ?.toLowerCase()}.png`}
              alt=""
            />
            {t(el)}
          </div>
        ))}
      </div>
      <div className="tournament-rules-form">
        <div className="bonus-form-content bonus-main-form-content bonus-locales-form-content">
          <div className="bonus-form-box bonus-locales-text-fields">
            <div className="bonus-locales-title-row">
              <div className="bonus-form-box--item">
                <InnerInput
                  value={currentItem?.title ?? ""}
                  placeholder={t("enterTitle")}
                  label={t("title")}
                  type="text"
                  id="tournament-locale-title"
                  disabled={!activeLocale || readOnly}
                  onChange={(value) => {
                    handleDataContent("title", value);
                    clearFieldError("title");
                  }}
                  hasError={Boolean(fieldErrors?.["title"])}
                />
              </div>

              <div className="bonus-form-switcher bonus-locales-active">
                <div className="title">{t("active")}</div>
                <SwitchBtn
                  isActive={!!currentItem?.isActive}
                  disabled={readOnly}
                  handleClick={() =>
                    handleDataContent(
                      "isActive",
                      currentItem?.isActive ? 0 : 1,
                    )
                  }
                />
              </div>
            </div>
            <div className="bonus-form-box--item">
              <InnerInput
                maxCodePoints={2500}
                value={currentItem?.description ?? ""}
                placeholder={t("enterText")}
                label={t("description")}
                type="textarea"
                id="tournament-locale-description"
                disabled={!activeLocale || readOnly}
                onChange={(value) => {
                  handleDataContent("description", value);
                  clearFieldError("description");
                }}
                hasError={Boolean(fieldErrors?.["description"])}
              />
            </div>
          </div>

          <div className="bonus-form-box bonus-locales-media-fields">
            <div className="bonus-form-img">
              <div className="bonus-images">
                <div className="bonus-image-item desktop">
                  <ImageUpload
                    label="Desktop"
                    labelClassName="bonus-main-form-label"
                    image={currentItem?.image}
                    allowMp4
                    size="large"
                    onAddImage={(file) => {
                      handleDataContent("image", file);
                      clearFieldError("image");
                    }}
                    isActive={true}
                    disabled={readOnly}
                    hasError={!!fieldErrors?.["image"]}
                  />
                </div>

                <div className="bonus-images-secondary">
                  <div className="bonus-image-item mobile">
                    <ImageUpload
                      label="Mobile"
                      labelClassName="bonus-main-form-label"
                      image={currentItem?.imageSmall}
                      allowMp4
                      size="large"
                      type="landscape"
                      aspect="21:9"
                      onAddImage={(file) => {
                        handleDataContent("imageSmall", file);
                        clearFieldError("imageSmall");
                      }}
                      isActive={true}
                      disabled={readOnly}
                      hasError={!!fieldErrors?.["imageSmall"]}
                    />
                  </div>

                  <div className="bonus-image-item bonus-portrait">
                    <ImageUpload
                      label="Portrait"
                      labelClassName="bonus-main-form-label"
                      image={currentItem?.portraitImage}
                      allowMp4
                      size="large"
                      type="portrait"
                      aspect="9:16"
                      onAddImage={(file) => {
                        handleDataContent("portraitImage", file);
                        clearFieldError("portraitImage");
                      }}
                      isActive={true}
                      disabled={readOnly}
                      hasError={!!fieldErrors?.["portraitImage"]}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bonus-form-box--item html-editor bonus-locales-content-editor">
            <div className="label">{t("content")}</div>
            <TextEditor
              value={currentItem?.content ?? ""}
              onChange={(e) => {
                handleDataContent("content", e);
                clearFieldError("content");
              }}
              disabled={!activeLocale || readOnly}
              hasError={Boolean(fieldErrors?.["content"])}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TournamentsContentTab;
